/**
 * Permission Middleware Utilities
 * Frontend implementation of permission checking middleware for protecting routes and UI elements
 * These are used to check permissions before rendering components or allowing actions
 */

import { adminHasPermission, getCurrentAdmin } from './authManager';

/**
 * Higher-Order Component (HOC) to check if admin has required role
 * @param {Array<string>} allowedRoles - Array of allowed roles (e.g., ['admin', 'super_admin'])
 * @returns {Function} HOC that wraps the component
 */
export const requireRole = (allowedRoles) => (WrappedComponent) => {
  return function RoleProtectedComponent(props) {
    const currentAdmin = getCurrentAdmin();

    if (!currentAdmin) {
      return null; // Or redirect to login
    }

    // Super admin bypasses role checks
    if (currentAdmin.isSuperAdmin || currentAdmin.role === 'super_admin') {
      return <WrappedComponent {...props} />;
    }

    if (!allowedRoles.includes(currentAdmin.role)) {
      return null; // Or show access denied component
    }

    return <WrappedComponent {...props} />;
  };
};

/**
 * Higher-Order Component (HOC) to check if admin has specific sub-role permission
 * @param {string} category - Permission category (e.g., 'users', 'groups', 'funds')
 * @param {string} permission - Specific permission (e.g., 'canView', 'canManage')
 * @returns {Function} HOC that wraps the component
 */
export const requireSubRolePermission = (category, permission) => (WrappedComponent) => {
  return function PermissionProtectedComponent(props) {
    const currentAdmin = getCurrentAdmin();

    if (!currentAdmin) {
      return null; // Or redirect to login
    }

    // Super admin bypasses sub-role permission checks
    if (currentAdmin.isSuperAdmin || currentAdmin.role === 'super_admin') {
      return <WrappedComponent {...props} />;
    }

    // Check if admin has the required permission
    const hasPermission = checkSubRolePermission(category, permission);

    if (!hasPermission) {
      return null; // Or show access denied component
    }

    return <WrappedComponent {...props} />;
  };
};

/**
 * Check if admin has a specific sub-role permission
 * @param {string} category - Permission category (e.g., 'users', 'groups', 'funds')
 * @param {string} permission - Specific permission (e.g., 'canView', 'canManage')
 * @param {Object} context - Additional context for permission checks (e.g., { groupId: '123' })
 * @returns {boolean} True if admin has the permission
 */
export const checkSubRolePermission = (category, permission, context = {}) => {
  const currentAdmin = getCurrentAdmin();

  if (!currentAdmin) {
    return false;
  }

  // Super admin has all permissions
  if (currentAdmin.isSuperAdmin || currentAdmin.role === 'super_admin') {
    return true;
  }

  // Check if admin has the permission in their permissions object
  const categoryPermissions = currentAdmin.permissions?.[category];

  if (!categoryPermissions) {
    return false;
  }

  // Check basic boolean permission
  if (typeof categoryPermissions[permission] === 'boolean') {
    return categoryPermissions[permission];
  }

  // Context-specific permission checks
  if (context.groupId && categoryPermissions.canViewSpecific) {
    return categoryPermissions.canViewSpecific.includes(context.groupId);
  }

  if (context.instrumentId && categoryPermissions.canViewSpecific) {
    return categoryPermissions.canViewSpecific.includes(context.instrumentId);
  }

  if (context.category && categoryPermissions.canViewByCategory) {
    return categoryPermissions.canViewByCategory.includes(context.category);
  }

  if (context.groups && categoryPermissions.canViewSpecificGroups) {
    // Check if admin has access to any of the provided groups
    const allowedGroups = categoryPermissions.canViewSpecificGroups;
    return context.groups.some(group => allowedGroups.includes(group));
  }

  return false;
};

/**
 * Check if admin has access to a specific group
 * @param {string} groupId - Group ID to check access for
 * @returns {boolean} True if admin can access the group
 */
export const requireGroupAccess = (groupId) => {
  const currentAdmin = getCurrentAdmin();

  if (!currentAdmin) {
    return false;
  }

  // Super admin has access to all groups
  if (currentAdmin.isSuperAdmin || currentAdmin.role === 'super_admin') {
    return true;
  }

  const groupsPermissions = currentAdmin.permissions?.groups;

  if (!groupsPermissions) {
    return false;
  }

  // If canView is true and no specific groups are restricted, allow access
  if (groupsPermissions.canView && !groupsPermissions.canViewSpecific) {
    return true;
  }

  // Check if group is in the specific allowed list
  if (groupsPermissions.canViewSpecific && groupsPermissions.canViewSpecific.includes(groupId)) {
    return true;
  }

  return false;
};

/**
 * Check if admin has access to a specific instrument
 * @param {string} instrumentId - Instrument ID to check access for
 * @param {string} category - Optional instrument category
 * @returns {boolean} True if admin can access the instrument
 */
export const requireInstrumentAccess = (instrumentId, category = null) => {
  const currentAdmin = getCurrentAdmin();

  if (!currentAdmin) {
    return false;
  }

  // Super admin has access to all instruments
  if (currentAdmin.isSuperAdmin || currentAdmin.role === 'super_admin') {
    return true;
  }

  const instrumentsPermissions = currentAdmin.permissions?.instruments;

  if (!instrumentsPermissions) {
    return false;
  }

  // If canView is true and no specific instruments are restricted, allow access
  if (instrumentsPermissions.canView && !instrumentsPermissions.canViewSpecific && !instrumentsPermissions.canViewByCategory) {
    return true;
  }

  // Check if instrument is in the specific allowed list
  if (instrumentsPermissions.canViewSpecific && instrumentsPermissions.canViewSpecific.includes(instrumentId)) {
    return true;
  }

  // Check if instrument category is in the allowed categories
  if (category && instrumentsPermissions.canViewByCategory && instrumentsPermissions.canViewByCategory.includes(category)) {
    return true;
  }

  return false;
};

/**
 * Get accessible groups for current admin
 * @param {Array} allGroups - Array of all available groups
 * @returns {Array} Array of group IDs that the admin can access
 */
export const getAccessibleGroups = (allGroups = []) => {
  const currentAdmin = getCurrentAdmin();

  if (!currentAdmin) {
    return [];
  }

  // Super admin has access to all groups
  if (currentAdmin.isSuperAdmin || currentAdmin.role === 'super_admin') {
    return allGroups;
  }

  const groupsPermissions = currentAdmin.permissions?.groups;

  if (!groupsPermissions || !groupsPermissions.canView) {
    return [];
  }

  // If no specific groups are restricted, admin can view all
  if (!groupsPermissions.canViewSpecific || groupsPermissions.canViewSpecific.length === 0) {
    return allGroups;
  }

  // Filter to only specific allowed groups
  return allGroups.filter(group =>
    groupsPermissions.canViewSpecific.includes(group.id || group._id || group)
  );
};

/**
 * Get accessible instruments for current admin
 * @param {Array} allInstruments - Array of all available instruments
 * @returns {Object} Object with instrumentIds array and categories array
 */
export const getAccessibleInstruments = (allInstruments = []) => {
  const currentAdmin = getCurrentAdmin();

  if (!currentAdmin) {
    return { instrumentIds: [], categories: [] };
  }

  // Super admin has access to all instruments
  if (currentAdmin.isSuperAdmin || currentAdmin.role === 'super_admin') {
    return {
      instrumentIds: allInstruments.map(i => i.id || i._id || i),
      categories: [...new Set(allInstruments.map(i => i.category))]
    };
  }

  const instrumentsPermissions = currentAdmin.permissions?.instruments;

  if (!instrumentsPermissions || !instrumentsPermissions.canView) {
    return { instrumentIds: [], categories: [] };
  }

  // Get specific instrument IDs
  let instrumentIds = [];

  if (!instrumentsPermissions.canViewSpecific || instrumentsPermissions.canViewSpecific.length === 0) {
    instrumentIds = allInstruments.map(i => i.id || i._id || i);
  } else {
    instrumentIds = allInstruments
      .filter(i => instrumentsPermissions.canViewSpecific.includes(i.id || i._id || i))
      .map(i => i.id || i._id || i);
  }

  // Get allowed categories
  const categories = instrumentsPermissions.canViewByCategory || [];

  return { instrumentIds, categories };
};

/**
 * Clear cached permissions for current admin
 * This should be called after sub-role assignments or changes
 * @param {string} adminId - Admin ID to clear permissions for (optional, defaults to current admin)
 */
export const clearCachedSubRolePermissions = (adminId = null) => {
  const currentAdmin = getCurrentAdmin();

  if (!currentAdmin) {
    return;
  }

  // If adminId is provided, only clear if it matches current admin
  if (adminId && currentAdmin.id !== adminId && currentAdmin._id !== adminId) {
    return;
  }

  // Clear the cached permissions from localStorage
  localStorage.removeItem('cachedPermissions');

  // IMPORTANT: Preserve admin's core identity (id, role, isSuperAdmin, username, email, profile)
  // Only clear the permissions object which comes from sub-roles
  // The admin's role and isSuperAdmin flag are inherent and don't come from sub-roles
  const preservedAdminData = {
    id: currentAdmin.id,
    _id: currentAdmin._id,
    username: currentAdmin.username,
    email: currentAdmin.email,
    role: currentAdmin.role,
    isSuperAdmin: currentAdmin.isSuperAdmin,
    profile: currentAdmin.profile,
    permissions: {} // Clear only the permissions from sub-roles
  };

  // Update adminUser with preserved core identity but cleared permissions
  localStorage.setItem('adminUser', JSON.stringify(preservedAdminData));

  // Dispatch event to notify components to refresh permissions
  window.dispatchEvent(new CustomEvent('permissions-cleared', {
    detail: { adminId: adminId || currentAdmin.id || currentAdmin._id }
  }));

  console.log('[PermissionMiddleware] Cleared cached permissions for admin:', adminId || currentAdmin.id);
  console.log('[PermissionMiddleware] Preserved core identity - role:', preservedAdminData.role, 'isSuperAdmin:', preservedAdminData.isSuperAdmin);
};

/**
 * Apply sub-role filters to data based on current admin's permissions
 * This is used to filter data arrays (users, groups, instruments, etc.) based on permissions
 * @param {string} category - Permission category (e.g., 'users', 'groups', 'instruments')
 * @param {Array} data - Array of data to filter
 * @returns {Array} Filtered array based on permissions
 */
export const applySubRoleFilters = (category, data = []) => {
  const currentAdmin = getCurrentAdmin();

  if (!currentAdmin || !data || data.length === 0) {
    return data;
  }

  // Super admin sees all data
  if (currentAdmin.isSuperAdmin || currentAdmin.role === 'super_admin') {
    return data;
  }

  const categoryPermissions = currentAdmin.permissions?.[category];

  if (!categoryPermissions || !categoryPermissions.canView) {
    return [];
  }

  // If no specific restrictions, return all data
  if (!categoryPermissions.canViewSpecific || categoryPermissions.canViewSpecific.length === 0) {
    return data;
  }

  // Filter data based on specific allowed IDs
  const allowedIds = categoryPermissions.canViewSpecific;

  return data.filter(item => {
    const itemId = item.id || item._id || item.groupId || item.instrumentId || item;
    return allowedIds.includes(itemId);
  });
};

// Export all utilities
export default {
  requireRole,
  requireSubRolePermission,
  checkSubRolePermission,
  requireGroupAccess,
  requireInstrumentAccess,
  getAccessibleGroups,
  getAccessibleInstruments,
  clearCachedSubRolePermissions,
  applySubRoleFilters
};
